import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const providerResyncAndOverridesGuide: AdminGuide = {
  id: "provider-resync-and-overrides",
  title: "Sync an organisation's accounting data",
  category: "Provider Sync & Data",
  audience: ["Support", "Ops"],
  summary:
    "Start a provider sync for a customer organisation from its record page. Staff with permission to manage accounting integrations can do this, and every sync is audited with your support note.",
  keywords: [
    "force sync",
    "sync data",
    "resync",
    "provider",
    "xero",
    "quickbooks",
    "full refresh",
    "reason",
    "audit",
  ],
  location: "Admin",
  risk: "high",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.ADMIN_API_DOCS,
    GUIDE_SOURCE_DOCS.ACCOUNTING_STANDARDS,
  ],
  warnings: [
    "A sync calls the customer's Xero or QuickBooks and can take several minutes. Only one sync can run for an organisation at a time.",
    "A support note is required. Write the ticket or case reference so someone else can see later why the sync ran.",
  ],
  safetyNotes: [
    "Start with a normal sync. Use a full refresh only when a normal sync has missed changes.",
    "The customer is not emailed about a sync you start.",
    "If you are unsure the organisation is the right one, stop and check first.",
  ],
  whatThisHelpsYouDo: [
    "Bring a customer's data up to date when they cannot or should not do it themselves, for example after they reconnect Xero.",
    "Re-download history with a full refresh when a normal sync left gaps.",
  ],
  whenToUse: [
    "A customer's data is stale and a manual sync is approved.",
    "Engineering asks Support/Ops to run a sync with a specific window.",
  ],
  whenNotToUse: [
    "You cannot clearly describe why the sync is needed (no ticket/case).",
    "The organisation's accounting connection needs attention. The customer has to reconnect first, or use **Fix accounting connection**.",
    "The organisation has not chosen cash or accrual yet. The sync is refused until they do.",
  ],
  beforeYouStart: [
    "The organisation's record page in Admin.",
    "A ticket/case reference and a clear reason in plain English.",
  ],
  steps: [
    "Open Admin → **Organisations** (`/organisations`) and open the organisation's record.",
    "Open the **⋯** menu and choose **Sync data**.",
    "Keep the default 3 years unless Engineering asked for a different window. Tick **Full refresh** only if a normal sync missed changes.",
    "In **Support note (required)**, write the ticket/case reference and why the sync is needed, then click **Start sync**.",
    "Watch **Recent sync activity** under **Connection and sync**. It updates every few seconds until the sync finishes.",
    "Record what you did in the ticket/case: organisation ID, window, and the time.",
  ],
  whatSuccessLooksLike: [
    "**Recent sync activity** shows the run as completed.",
    "The Data sync line reads **Last synced** with today's date.",
  ],
  troubleshooting: [
    {
      title: "Sync data is greyed out or missing",
      body: [
        "Greyed out: you need permission to manage accounting integrations. Ask a support lead.",
        "Missing: the organisation has no accounting connection, so there is nothing to sync.",
      ],
    },
    {
      title: "A sync is already running",
      body: [
        "Wait for the current run to finish in **Recent sync activity**, then try again if you still need to.",
      ],
    },
    {
      title: "The sync failed or the data is still stale",
      body: [
        "Check the failure shown in **Recent sync activity**. A connection error usually means the customer must reconnect.",
        "Collect the organisation ID, provider (Xero/QuickBooks) and the time you started the sync, then escalate to Engineering.",
      ],
    },
  ],
  escalateWhen: [
    "You are unsure the sync is safe or needed.",
    "The organisation's data looks wrong or cannot sync cleanly.",
    "The problem repeats after more than one sync.",
  ],
  relatedGuides: [
    {
      label: "Accounting method (cash vs accrual)",
      guideId: "accounting-method-cash-vs-accrual",
    },
    { label: "Submitting a bug report", guideId: "bug-reporting" },
  ],
  relatedAdminPaths: [{ label: "Organisations", path: "/organisations" }],
};
